import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext.js';
import Admin from './pages/Admin.js';
import Archive from './pages/Archive.js';
import Chat from './pages/Chat.js';
import Claims from './pages/Claims.js';
import Compliance from './pages/Compliance.js';
import Dashboard from './pages/Dashboard.js';
import Documents from './pages/Documents.js';
import ForgotPassword from './pages/ForgotPassword.js';
import Insurance from './pages/Insurance.js';
import Login from './pages/Login.js';
import Mfa from './pages/Mfa.js';
import NewOrder from './pages/NewOrder.js';
import Offers from './pages/Offers.js';
import Orders from './pages/Orders.js';
import Pricing from './pages/Pricing.js';
import Products from './pages/Products.js';
import Register from './pages/Register.js';
import Registry from './pages/Registry.js';
import Security from './pages/Security.js';
import SetPassword from './pages/SetPassword.js';
import SupplyOrderDetail from './pages/SupplyOrderDetail.js';
import SupplyOrders from './pages/SupplyOrders.js';
import Tasks from './pages/Tasks.js';
import Traceability from './pages/Traceability.js';

function FullScreenLoader() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-full items-center justify-center text-sm text-slate-500 dark:text-slate-400">
      {t('status.loading')}
    </div>
  );
}

function RequireAuth({ children }: { children: ReactElement }) {
  const { user, loading, mfaPending } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (mfaPending || user.mfaSetupRequired) return <Navigate to="/mfa" replace />;
  return children;
}

export default function App() {
  const { loading } = useAuth();
  if (loading) return <FullScreenLoader />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/mfa" element={<Mfa />} />
      <Route path="/register" element={<Register />} />
      <Route path="/accept-invite" element={<SetPassword mode="invite" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<SetPassword mode="reset" />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/products"
        element={
          <RequireAuth>
            <Products />
          </RequireAuth>
        }
      />
      <Route
        path="/orders"
        element={
          <RequireAuth>
            <Orders />
          </RequireAuth>
        }
      />
      <Route
        path="/orders/new"
        element={
          <RequireAuth>
            <NewOrder />
          </RequireAuth>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <Admin />
          </RequireAuth>
        }
      />
      {(
        [
          ['/offers', Offers],
          ['/supply-orders', SupplyOrders],
          ['/supply-orders/:id', SupplyOrderDetail],
          ['/pricing', Pricing],
          ['/documents', Documents],
          ['/traceability', Traceability],
          ['/claims', Claims],
          ['/tasks', Tasks],
          ['/registry', Registry],
          ['/compliance', Compliance],
          ['/insurance', Insurance],
          ['/archive', Archive],
          ['/chat', Chat],
          ['/settings/security', Security],
        ] as const
      ).map(([path, Component]) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <Component />
            </RequireAuth>
          }
        />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
